import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, HandHeart, CheckCircle2, Building2 } from "lucide-react";
import { api } from "../lib/api";

const VOLUNTEER_ROLES = ["Foster", "Transportation", "Supplies / Donations", "Veterinary / Medical", "Other volunteer"];
const input = "w-full border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-zinc-900 bg-white";

const VARIANTS = {
  rescue: {
    blank: { role: "Rescue organization", organization: "", name: "", phone: "", email: "", service_area: "", availability: "", how_i_can_help: "" },
    fields: [
      ["organization", "Organization name *", "text"],
      ["name", "Contact person *", "text"],
      ["phone", "Phone *", "tel"],
      ["email", "Email *", "email"],
      ["service_area", "Location (city / county) *", "text"],
    ],
    helpLabel: "How your organization can assist *",
    submit: "Send to Paws on Record",
    Icon: Building2,
  },
  volunteer: {
    blank: { role: "Foster", organization: "", name: "", phone: "", email: "", service_area: "", availability: "", how_i_can_help: "" },
    fields: [
      ["name", "Your name *", "text"],
      ["email", "Email *", "email"],
      ["phone", "Phone", "tel"],
      ["service_area", "ZIP / area you cover", "text"],
      ["availability", "Availability", "text"],
    ],
    helpLabel: "How I can help *",
    submit: "Send my offer",
    Icon: HandHeart,
  },
};

export default function HelpOfferForm({ rescueId, variant = "volunteer", disabled = false, onDone }) {
  const v = VARIANTS[variant];
  const [f, setF] = useState(v.blank);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return toast.info("Draft preview — forms open once you publish this campaign.");
    setBusy(true);
    try {
      await api.post(`/urgent-rescues/${rescueId}/help-offer`, f);
      setDone(true);
      toast.success("Thank you! Paws on Record received your message.");
      onDone?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please complete all required fields with a valid email.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="border-2 border-green-700 bg-green-50 p-6" data-testid={`${variant}-form-success`}>
        <CheckCircle2 className="text-green-700 mb-2" />
        <div className="font-display font-black text-xl">Thank you for stepping up.</div>
        <p className="text-sm text-zinc-700 mt-1">The Paws on Record team will contact you directly. If animals are in immediate danger, call local Animal Control.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${variant}-form`}>
      {variant === "volunteer" && (
        <div>
          <label className="text-xs font-bold uppercase tracking-widest block mb-1">I can help as *</label>
          <select value={f.role} onChange={set("role")} className={input} data-testid="volunteer-form-role">
            {VOLUNTEER_ROLES.map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {v.fields.map(([k, label, type]) => (
          <div key={k}>
            <label className="text-xs font-bold uppercase tracking-widest block mb-1">{label}</label>
            <input type={type} value={f[k]} onChange={set(k)} className={input} required={label.endsWith("*")} data-testid={`${variant}-form-${k}`} />
          </div>
        ))}
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-widest block mb-1">{v.helpLabel}</label>
        <textarea rows={4} value={f.how_i_can_help} onChange={set("how_i_can_help")} className={input} required minLength={5} data-testid={`${variant}-form-message`} />
      </div>
      <button type="submit" disabled={busy} className="btn-danger w-full justify-center disabled:opacity-60" data-testid={`${variant}-form-submit`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <v.Icon size={14} />} {v.submit}
      </button>
      <p className="text-[11px] text-zinc-500">Your details go only to the Paws on Record team — they are never published.</p>
    </form>
  );
}
